/**
 * Garden tile art — modern flat plant stages on shared ground.
 *
 * Rolling stages (from growth.js):
 *   week    → met in the last 7 days (fruit color stable per person)
 *   month   → last ~30 days
 *   quarter → last ~90 days
 *   year    → last ~365 days
 *   older   → before that / never
 */

export const STAGES = ["week", "month", "quarter", "year", "older"];

/** Fruit color variants for the healthiest (week) stage. */
export const WEEK_FRUIT_COLORS = ["red", "orange", "yellow", "purple"];

/** Garden map: always 4 tiles wide. Empty DB → 4×4 empty land. */
export const GARDEN_COLS = 4;
export const GARDEN_EMPTY_ROWS = 4;

/** @deprecated kept for any leftover imports */
export const DEFAULT_LAND_ATLAS = "modern";

/**
 * Legacy state aliases → current stages (old API / bookmarks).
 */
const LEGACY_STATE = {
  tree: "week",
  flower: "month",
  grass: "quarter",
  sand: "year",
  stone: "older",
  barren: "older",
  sprouting: "year",
  leafy: "quarter",
  bloom: "week",
  dormant: "older",
};

export const STAGE_LABELS = {
  week: "week",
  month: "month",
  quarter: "quarter",
  year: "year",
  older: "older",
};

/**
 * @param {Array<{ id: string, name: string, state: string }>} people
 */
export function buildGardenCells(people = []) {
  const list = Array.isArray(people) ? people : [];
  const rows = Math.max(
    GARDEN_EMPTY_ROWS,
    Math.ceil(list.length / GARDEN_COLS) || GARDEN_EMPTY_ROWS,
  );
  const total = rows * GARDEN_COLS;
  return Array.from({ length: total }, (_, i) => {
    const person = list[i] || null;
    return {
      key: person ? `person-${person.id}` : `land-${i}`,
      person,
    };
  });
}

export function normalizeStage(stageOrState) {
  const raw = String(stageOrState || "");
  if (STAGES.includes(raw)) return raw;
  if (LEGACY_STATE[raw]) return LEGACY_STATE[raw];
  return "older";
}

export function stateToStage(state) {
  return normalizeStage(state);
}

export function stageLabel(stageOrState) {
  const stage = normalizeStage(stageOrState);
  return STAGE_LABELS[stage] || STAGE_LABELS.older;
}

/** Stable hash → index (same person always same fruit color). */
export function stableHash(key = "") {
  const s = String(key);
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Prefer person id; fall back to name. */
export function weekFruitColor(personKey = "") {
  const key = String(personKey || "").trim() || "default";
  return WEEK_FRUIT_COLORS[stableHash(key) % WEEK_FRUIT_COLORS.length];
}

/** Empty plantable land. */
export function groundTileUrl(_stage) {
  return "/assets/tiles/modern/empty.svg";
}

/**
 * Person plant tile for a growth state.
 * @param {string} name
 * @param {string} state
 * @param {{ personId?: string } | string} [opts] personId or legacy unused atlas string
 */
export function personTileUrl(name, state, opts) {
  const stage = normalizeStage(state);
  if (stage === "week") {
    const personKey =
      (opts && typeof opts === "object" && opts.personId) ||
      name ||
      "";
    const color = weekFruitColor(personKey);
    return `/assets/tiles/modern/week-${color}.svg`;
  }
  return `/assets/tiles/modern/${stage}.svg`;
}

export function personTileFallbackUrl(state, opts) {
  return personTileUrl("", state, opts);
}

/** @deprecated letter art removed from map */
export function hedgeLetter(name = "") {
  const ch = String(name)
    .trim()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .match(/[A-Za-z]/);
  return ch ? ch[0].toUpperCase() : "?";
}

/** @deprecated */
export function hasPaintedLetter() {
  return true;
}

/** @deprecated */
export function letterTileUrl() {
  return groundTileUrl();
}
